import { useEffect, useState } from 'react';
import { getDocument } from '../functions';

// Probe which CRM is connected for a company. Order matters — GHL first, then FUB,
// then Salesforce — matching the legacy LeadBuyerMatches.js behaviour so existing
// users with multiple credentials connected see the same selection.
export function useConnectedCrm(companyId) {
    const [connectedCRM, setConnectedCRM] = useState(null);
    const [crmLoading, setCrmLoading] = useState(true);

    useEffect(() => {
        if (!companyId) return;

        let cancelled = false;
        setCrmLoading(true);

        (async () => {
            try {
                // The three probes are independent — only the PRIORITY of the
                // answer matters (GHL > FUB > Salesforce). All three are
                // started at once so the FUB / Salesforce / no-CRM cases cost
                // one round trip instead of three, but GHL (the priority and
                // the common case) is awaited FIRST and settles the answer on
                // its own, so a GHL company is not held on the slowest read.
                // getDocument never rejects (it resolves {status:400}), so a
                // missing doc on any leg cannot fail the others.
                const ghlP = getDocument('ghl_accounts', companyId);
                const fubP = getDocument('follow_up_boss_accounts', companyId);
                const sfP = getDocument('salesforce_accounts', companyId);

                const ghl = await ghlP;
                if (cancelled) return;
                const ghlData = ghl.status === 200 ? ghl.data?.data : null;
                if (ghlData?.locationId && Array.isArray(ghlData.locationId) && ghlData.locationId.length > 0) {
                    setConnectedCRM('ghl');
                    return;
                }

                const [fub, sf] = await Promise.all([fubP, sfP]);
                if (cancelled) return;
                if (fub.status === 200 && fub.data?.data?.integration === true) {
                    setConnectedCRM('fub');
                } else if (sf.status === 200 && sf.data?.data?.integration === true) {
                    setConnectedCRM('salesforce');
                } else {
                    setConnectedCRM(null);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Error detecting CRM:', err);
                if (!cancelled) setConnectedCRM(null);
            } finally {
                if (!cancelled) setCrmLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [companyId]);

    return {
        connectedCRM,
        crmLoading,
        hasCrmIntegration: connectedCRM !== null,
    };
}
